<template>
  <div class="row items-center toolbar-fields-row">
    <hr-divider
      vertical
      inset
    />

    <hr-toolbar-dropdown
      :label="label"
      @hide="$emit('hide')"
    >
      <hr-list
        dense
        class="scrollable-height"
      >
        <hr-list-item>
          <hr-text-field
            v-model="searchQuery"
            dense
            outlined
            clearable
            hide-bottom-space
            :label="$t('common.buttonSearch')"
            autofocus
            @clear="searchQuery = ''"
          >
            <template #prepend>
              <hr-icon name="search" />
            </template>
          </hr-text-field>
        </hr-list-item>
        <hr-list-item
          v-for="field in filteredOrphanFields"
          :key="field.token"
          v-close-popup
          clickable
          :label="field.label"
          @click="onInsertToken(field)"
        >
          <template #trailing>
            <hr-icon
              v-if="field.type === 'imageFile'"
              name="image"
            />
            <hr-icon-action-btn
              v-if="field.canEdit"
              icon="edit"
              @click.stop="onEditField(field.id)"
            />
          </template>
        </hr-list-item>

        <hr-divider
          v-if="orphanFields.length > 0"
        />
        <hr-expansion-item
          v-for="fieldCategory in filteredFieldCategoryFolders"
          :key="`${fieldCategory.categoryIdentifier}-${searchQuery !== ''}`"
          :default-opened="searchQuery !== ''"
          clickable
          expand-separator
          :label="fieldCategory.categoryLabel"
        >
          <hr-list-item
            v-for="field in fieldCategory.fields"
            :key="field.token"
            v-close-popup
            clickable
            :inset-level="0.5"
            :label="field.label"
            @click="onInsertToken(field)"
          >
            <template #trailing>
              <hr-icon
                v-if="field.type === 'imageFile'"
                name="image"
              />
              <hr-icon-action-btn
                v-if="field.canEdit"
                icon="edit"
                @click.stop="onEditField(field.id)"
              />
            </template>
          </hr-list-item>
        </hr-expansion-item>
      </hr-list>
    </hr-toolbar-dropdown>
    <template
      v-if="!noCustomFields"
    >
      <hr-loading-spinner
        v-if="isLoading"
      />
      <hr-toolbar-btn
        v-else
        icon="add_box"
        data-e2e="button-add-field"
        :label="addButtonLabel"
        @click="onAddNewField"
      />
    </template>
  </div>
</template>
<script>
import { chain, reject } from 'lodash';
import {
  HrDivider, HrExpansionItem, HrIcon, HrIconActionBtn, HrList,
  HrListItem, HrLoadingSpinner, HrTextField, HrToolbarBtn, HrToolbarDropdown,
} from 'src/design-system';

export default {
  name: 'TextEditorFieldsToolbar',

  components: {
    HrToolbarBtn,
    HrDivider,
    HrExpansionItem,
    HrIcon,
    HrList,
    HrListItem,
    HrLoadingSpinner,
    HrTextField,
    HrIconActionBtn,
    HrToolbarDropdown,
  },

  props: {
    label: {
      type: String,
      default: '',
    },

    addButtonLabel: {
      type: String,
      default: '',
    },

    fieldCategories: {
      type: Array,
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    noCustomFields: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['insert', 'edit', 'add', 'hide'],

  data() {
    return {
      searchQuery: '',
    };
  },

  computed: {

    filteredOrphanFields() {
      const lowerQuery = this.searchQuery.toLowerCase();
      return this.orphanFields.filter((field) => (
        field.label.trim().toLowerCase().includes(lowerQuery) && !field?.hiddenFromSelection
      ));
    }, // filteredOrphanFields()

    filteredFieldCategoryFolders() {
      const lowerQuery = this.searchQuery.toLowerCase();
      return this.fieldCategoryFolders.map((folder) => {
        const filteredFields = folder.fields.filter((field) => (
          field.label.trim().toLowerCase().includes(lowerQuery) && !field?.hiddenFromSelection
        ));

        return {
          ...folder,
          fields: filteredFields,
        };
      }).filter((folder) => folder.fields.length > 0);
    }, // filteredFieldCategoryFolders()

    fieldCategoryFolders() {
      return reject(this.fieldCategories, { categoryIdentifier: null });
    },

    orphanFields() {
      return chain(this.fieldCategories)
        .find({ categoryIdentifier: null })
        .get('fields', [])
        .value();
    },

  },

  methods: {

    onInsertToken(token) {
      this.$emit('insert', token);
    }, // onInsertToken()

    onEditField(fieldId) {
      this.$emit('edit', fieldId);
    }, // onEditField()

    onAddNewField() {
      this.$emit('add');
    },
  },

};
</script>

<style scoped>
.toolbar-fields-row {
  gap: 2px;
}

.scrollable-height {
  max-height: 80vh;
}
</style>
